<template>
  <div class="relative min-h-screen">
    <div
      v-if="systemStore.clientSettings.loading"
      class="absolute top-0 left-0 z-50 text-center p-4 flex items-center justify-center w-full h-screen"
    >
      <UiLoader size="80px" />
    </div>
    <router-view v-slot="{ Component }">
      <transition
        mode="out-in"
        appear
        enter-active-class="transition ease-out duration-150 transform"
        enter-from-class="opacity-0 translate-x-4"
        enter-to-class="opacity-100"
        leave-active-class="ease-in duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0 -translate-x-4"
      >
        <component :is="Component" />
      </transition>
    </router-view>

    <div class="toast-notifications fixed top-2 right-2 z-[9999] flex flex-col-reverse gap-3">
      <transition-group
        name="toast-notification"
        tag="div"
        enter-active-class="transition ease-out duration-300 transform"
        enter-from-class="opacity-0 -translate-y-10 scale-50"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="ease-in duration-200"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 -translate-y-10 translate-y-0 scale-50"
      >
        <pe-toast
          v-for="item in notificationStore.toasts"
          :key="item.id"
          :id="item.id || ''"
          :type="item.type"
          :title="item.title"
          :message="item.message"
          :icon="item.icon"
          :auto-close="item.autoClose"
          :duration="item.duration"
          :closeable="item.closeable"
          :onClick="item.onClick"
          @close="() => notificationStore.dismissToast(item.id || '')"
          class="mx-4 mt-4"
        />
      </transition-group>
    </div>

    <pe-dialog
      v-for="item in notificationStore.dialogs"
      :key="item.id"
      auto
      :title="item.title"
      :message="item.message"
      :alt="item.alt"
      :hideImage="item.hideImage"
      :hideCancelButton="item.hideCancelButton"
      :cancelButtonText="item.cancelButtonText"
      :confirmButtonText="item.confirmButtonText"
      @on-confirm="
        () => {
          notificationStore.dismissDialog(item.id || '')
          if (typeof item.complete === 'function') item.complete()
        }
      "
      @on-cancel="
        () => {
          notificationStore.dismissDialog(item.id || '')
          if (typeof item.cancel === 'function') item.cancel()
        }
      "
    />

    <UiDialog
      v-for="item in notificationStore.newDialogs"
      :key="item.id"
      :title="item.title"
      :message="item.message"
      :id="item.id"
      :withTextarea="item.withTextarea"
      :complete="item.complete"
      :cancel="item.cancel"
      :type-confirm-button="item.typeConfirmButton"
      :text-confirm-button="item.textConfirmButton"
      :immediate="item.immediate ?? true"
    />

    <ModalsContainer />
    <LangSelectorDialog ref="langSelector" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, provide, ref, watch, watchEffect, onMounted } from 'vue'
import { useFavicon, useTitle } from '@vueuse/core'
import { ModalsContainer } from 'vue-final-modal'
import tinycolor from 'tinycolor2'
import { useSystemStore, useNotificationsStore } from '@/stores'
import { i18N, toast } from './common/util'

import peToast from '@/components/peToast/index.vue'
import peDialog from '@/components/PeDialog.vue'
import UiDialog from '@/components/ui/dialog.vue'
import UiLoader from '@/components/ui/loader.vue'

import LangSelectorDialog from '@/components/modais/langSelector.vue'

import TransparentLogo from '@/assets/logo_transparent.png'

const systemStore = useSystemStore()
const notificationStore = useNotificationsStore()

const systemUpdate = ref({
  hasUpdate: false,
  hash: ''
})

const showClassMenu = ref(true)
const langSelector = ref<InstanceType<typeof LangSelectorDialog>>()

const openLangSelector = () => langSelector.value?.open()

provide('app.showClassMenu', showClassMenu)
provide('app.openLangSelector', openLangSelector)
provide('app.systemUpdate', systemUpdate)

// Tema escuro/Claro
watchEffect(() => {
  document.querySelector('body')?.classList.toggle('dark', systemStore.isDark)
})

useTitle(computed(() => systemStore.getConfig.page?.title || 'Educação Corporativa'))
useFavicon(computed(() => systemStore.getConfig.logos?.icon || TransparentLogo))

const colorVariations = [28, 24, 16, 8, 0, -8, -16, -24, -28]

const primaryShades = computed(() =>
  colorVariations.map((variation) =>
    tinycolor(systemStore.getConfig.colors.interface?.primary || '#9E9E9E')
      .lighten(variation)
      .toHexString()
  )
)

watch(
  () => systemStore.getConfig.colors.interface,
  () => {
    const darkestShade = tinycolor(systemStore.getConfig.colors.interface?.primary || '#9E9E9E')
      .darken(50)
      .toHexString()

    primaryShades.value?.forEach((s, idx) => {
      document.documentElement.style.setProperty(`--c-primary-${idx + 1}00`, s)

      // Auto text color
      if (systemStore.getConfig.colors.interface.autoFontColor) {
        document.documentElement.style.setProperty(`--c-primary-at-${idx + 1}00`, tinycolor(s).isLight() ? darkestShade : '#FDFDFD')
      } else {
        document.documentElement.style.setProperty(`--c-primary-at-${idx + 1}00`, systemStore.getConfig.colors.interface.textColor || '#FDFDFD')
      }
    })
  },
  { immediate: true, deep: true }
)

interface UserScript {
  name?: string
  isLink?: boolean
  content?: string
  fixed?: boolean
}

const appendScript = (script: UserScript) => {
  const scriptTag = document.createElement('script')
  scriptTag.type = 'text/javascript'

  if (script.content?.trim().length === 0) return

  if (!script.isLink) {
    scriptTag.innerHTML = script.content || ''
  } else {
    scriptTag.src = script.content || ''
  }
  document.head.appendChild(scriptTag)
}

const checkUpdate = async () => {
  try {
    if ((window as any)?.configuration?.env !== 'Production') return

    let res = await fetch(window.location.origin + `/index.html?t=${new Date().getTime()}`)
    let str = await res.text()

    let strFind = 'src="/app.'
    let start = str.indexOf(strFind)
    let hash = str.substring(start + strFind.length, start + strFind.length + 16)

    let vers = localStorage.getItem('version')

    if (vers && vers !== hash) {
      systemUpdate.value.hasUpdate = true
      systemUpdate.value.hash = hash

      toast.open({
        type: 'warning',
        title: i18N('feedback.updateAvailable'),
        message: i18N('feedback.updateAvailableMessage'),
        autoClose: false,
        closeable: false,
        onClick: () => reload()
      })
    }

    localStorage.setItem('version', hash)
  } catch (error) {
    console.log('att', error)
  }
}

const reload = () => {
  window.location.reload()
}

onBeforeMount(() => {
  systemStore.setLanguage()

  // @FIXME: SXNzbyBhcXVpIMOpIGdhbWJpYXJyYSwgYWNoZSB1bWEgc29sdcOnw6NvIG1lbGhvcg==
  systemStore.clientSettings.settings?.defaultScripts?.map((script) => {
    if (typeof script.condition !== 'undefined' && !script.condition)return

    appendScript(script as any)
  })

  checkUpdate()
})

onMounted(() => {
  systemStore
    .loadParams()
    .then(() => {
        systemStore.clientSettings.settings?.scripts?.map((script) => {
          appendScript(script as any)
        })
      })
      .catch(() => {})
})
</script>

<style lang="scss">
html {
  background-color: black;
}

#app {
  width: 100%;
  height: 100%;
}
</style>
